import { useEffect, useMemo, useState } from 'react';
import { Search, Package, Filter, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import EmptyState from '../components/EmptyState';

interface Consumable {
  id: string;
  name: string;
  unit: string;
  default_price: number;
  category?: string;
  source?: string;
}

export default function Consumables() {
  const [items, setItems] = useState<Consumable[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/consumables');
      setItems(res.data || []);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const categories = useMemo(() => {
    const s = new Set<string>();
    items.forEach((i) => i.category && s.add(i.category));
    return ['All', ...Array.from(s).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (cat !== 'All') list = list.filter((i) => i.category === cat);
    const ql = q.trim().toLowerCase();
    if (ql) list = list.filter((i) => i.name.toLowerCase().includes(ql));
    return list;
  }, [items, q, cat]);

  const learnedCount = items.filter((i) => i.source === 'xero_learned').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Consumables</h1>
          <p className="text-slate-500 mt-1">
            Parts and materials used on jobs — pricing is auto-learned from Xero.
          </p>
        </div>
        {learnedCount > 0 && (
          <div className="chip bg-purple-100 text-purple-800 ring-1 ring-inset ring-purple-200">
            <Sparkles className="h-3 w-3" />
            {learnedCount} learned from Xero
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            className="input pl-9"
            placeholder="Search parts…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <select
            className="input pl-9 pr-8 appearance-none min-w-[200px] cursor-pointer"
            value={cat}
            onChange={(e) => setCat(e.target.value)}
          >
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="animate-spin h-6 w-6 rounded-full border-3 border-brand-600 border-t-transparent mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-8">
          <EmptyState
            icon={Package}
            title="No parts found"
            subtitle={q ? 'Try a different search.' : 'Run "Learn parts from Xero" in Settings to auto-populate.'}
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left font-semibold text-slate-500 uppercase tracking-wider text-xs px-6 py-3">
                    Name
                  </th>
                  <th className="text-left font-semibold text-slate-500 uppercase tracking-wider text-xs px-6 py-3">
                    Category
                  </th>
                  <th className="text-left font-semibold text-slate-500 uppercase tracking-wider text-xs px-6 py-3">
                    Unit
                  </th>
                  <th className="text-right font-semibold text-slate-500 uppercase tracking-wider text-xs px-6 py-3">
                    Typical price
                  </th>
                  <th className="text-left font-semibold text-slate-500 uppercase tracking-wider text-xs px-6 py-3">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-6 py-3">
                      <div className="font-semibold text-slate-900">{c.name}</div>
                    </td>
                    <td className="px-6 py-3 text-slate-600">
                      {c.category ? (
                        <span className="chip bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200">
                          {c.category}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-slate-600 text-xs font-mono">{c.unit}</td>
                    <td className="px-6 py-3 text-right font-mono font-semibold text-slate-900">
                      {c.default_price > 0 ? `£${c.default_price.toFixed(2)}` : '—'}
                    </td>
                    <td className="px-6 py-3">
                      {c.source === 'xero_learned' ? (
                        <span className="chip bg-purple-100 text-purple-800 ring-1 ring-inset ring-purple-200 text-[10px]">
                          <Sparkles className="h-3 w-3" /> Xero
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">Manual</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="md:hidden divide-y divide-slate-100">
            {filtered.map((c) => (
              <div key={c.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                      {c.category && <span>{c.category}</span>}
                      <span>·</span>
                      <span className="font-mono">{c.unit}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-bold text-slate-900">
                      {c.default_price > 0 ? `£${c.default_price.toFixed(2)}` : '—'}
                    </div>
                    {c.source === 'xero_learned' && (
                      <div className="text-[10px] text-purple-600 mt-0.5 flex items-center gap-0.5 justify-end">
                        <Sparkles className="h-2.5 w-2.5" /> Xero
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
